/**
 * NotificationsPage — Settings → Notifications.
 *
 * G2 spec: Keep ONLY the wired e-sign preference. Remove all other notification toggles.
 * G3: E-sign approved/rejected → in-app record + email to sender.
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { SectionCardHeader } from '../ui/SectionCard';
import { Alert } from '../ui/Alert';
import { useToast } from '../ui';
import { settingsAPI } from '../../services/api';
import useTelemetry from '../../hooks/useTelemetry';
import PageHeader from '../ui/PageHeader';
import { BellIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';

export default function NotificationsPage() {
  const { emit } = useTelemetry();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [esignEmail, setEsignEmail] = useState(true);
  // §6: customer SMS arrival reminders (compliance opt-in captured at e-sign)
  const [smsArrivalReminders, setSmsArrivalReminders] = useState(false);

  useEffect(() => {
    async function loadNotifications() {
      try {
        setLoading(true);
        const data = await settingsAPI.getNotifications();
        if (data?.email?.esign !== undefined) setEsignEmail(data.email.esign);
        if (data?.sms?.arrival_reminders !== undefined) setSmsArrivalReminders(data.sms.arrival_reminders);
      } catch {
        setError('Failed to load notification preferences.');
      } finally {
        setLoading(false);
      }
    }
    loadNotifications();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await settingsAPI.updateNotifications({
        email: { esign: esignEmail },
        sms: { arrival_reminders: smsArrivalReminders },
      });
      emit('notifications_preferences_saved', { esign: esignEmail, sms_arrival_reminders: smsArrivalReminders });
      toast.success('Notification preferences saved.');
    } catch {
      setError('Failed to save notification preferences.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-32 w-full rounded animate-pulse" style={{ backgroundColor: 'var(--neutral-100)' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Notification Preferences" subtitle="Manage your e-signature notification settings." />

      {error && <Alert variant="error">{error}</Alert>}

      {/* G3: E-sign notification — the ONLY wired preference */}
      <Card>
        <SectionCardHeader
          title="E-Signature Notifications"
          description="Receive notifications when a customer approves or declines a quote signature."
          icon={BellIcon}
        />
        <div className="px-6 pb-6">
          <div className="flex items-center justify-between py-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                E-sign approved/rejected
              </p>
              <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                Email notification sent to the quote sender when a customer responds to a signature request.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={esignEmail}
              onClick={() => setEsignEmail(!esignEmail)}
              className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              style={{ backgroundColor: esignEmail ? 'var(--color-action-primary)' : 'var(--neutral-300)' }}
            >
              <span
                className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white ring-0 transition duration-200 ease-in-out"
                style={{ transform: esignEmail ? 'translateX(20px)' : 'translateX(0)' }}
              />
            </button>
          </div>
        </div>
      </Card>

      {/* §6 Customer Notifications — SMS opt-in is compliance, not preference.
          Channels (SMS / email) are kept fully separate (§6.3) so an email-
          preferences block can land later without rework. */}
      <Card>
        <SectionCardHeader
          title="Customer Notifications — SMS"
          description='Send customers a "crew arriving" text 30–60 min before a work order. Opt-in required at quote acceptance.'
          icon={DevicePhoneMobileIcon}
        />
        <div className="px-6 pb-6">
          <div className="flex items-center justify-between py-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                Arrival reminder texts
              </p>
              <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                Enable the SMS arrival-reminder program for customers who have confirmed opt-in.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={smsArrivalReminders}
              onClick={() => setSmsArrivalReminders(!smsArrivalReminders)}
              className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              style={{ backgroundColor: smsArrivalReminders ? 'var(--color-action-primary)' : 'var(--neutral-300)' }}
            >
              <span
                className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white ring-0 transition duration-200 ease-in-out"
                style={{ transform: smsArrivalReminders ? 'translateX(20px)' : 'translateX(0)' }}
              />
            </button>
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </div>
  );
}
